import { gql } from '@apollo/client';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';
import { useMemo, useRef, useState } from 'react';
import { Container, Image, HeadlineAnim } from '@components';
import { getLocationLabel } from './detail-hero-utils';
import { DetailHeroFlexibleContentFragment } from "@graphqlTypes";
import { usePageWrapperData } from '@/providers/PageWrapperContext';

import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

import useParallax from '../Parallax/useParallax';

import styles from './DetailHero.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

const DetailItem = ({ text, subtext }: { text: string; subtext?: string }) => {
  return (
    <div className={cx("detail-item")}>
      <p className="text-l">{text}</p>
      {subtext && <p className="text-s">{subtext}</p>}
    </div>
  );
}

export default function DetailHeroFlexibleContent(props: DetailHeroFlexibleContentFragment) {
  const { headline, image, video, assetType, videoThumbnail, location, locationSubtext } = props;
  const { labels } = usePageWrapperData(); 
  const locationLabel = getLocationLabel(location[0], labels);
  const isVideo = useMemo(() => assetType[0] === 'video' && video, [assetType, video]);

  // Element refs
  const rootEl = useRef<HTMLDivElement>(null);
  const overlayEl = useRef<HTMLDivElement>(null);
  const imageEl = useRef<HTMLImageElement>(null);
  const headlineEl = useRef<HTMLHeadingElement>(null);
  const detailsEl = useRef<HTMLDivElement>(null);
  const bottomEl = useRef<HTMLDivElement>(null);

  // -------------------- Animation

  const timelineAnimIn = useRef<gsap.core.Timeline | null>(null);
  const [isHeadlineAnimGo, setIsHeadlineAnimGo] = useState(false);

  // Timeline initialization
  useGSAP(() => {

    // If timeline already exists, kill it to reset
    if (timelineAnimIn.current) timelineAnimIn.current.kill();

    // Create timeline!
    timelineAnimIn.current = gsap.timeline({ paused: true }).add('start');

    const heroTl = timelineAnimIn.current;

    const gDur = 0.8; // General duration for animations

    // ---------- Image
    if (imageEl.current) {
      heroTl.add('image-in');
      heroTl.fromTo(
        imageEl.current,
        {
          scale: 1.5,
        }, {
          scale: 1,
          duration: gDur*2,
          ease: 'power3.out',
        },
        'image-in'
      );
    }

    // ---------- Overlay
    if (overlayEl.current) {
      heroTl.fromTo(
        overlayEl.current,
        {
          opacity: 1
        }, {
          opacity: 0.6,
          duration: gDur*2,
          ease: 'power3.out',
        },
        'image-in+=0.1'
      );
    }

    // ---------- Headline
    if (headlineEl.current) {
      heroTl.add('headline-in', `'-=${gDur*1.5}'`);
      heroTl.fromTo(
        headlineEl.current,
        {
          opacity: 0,
          y: '2rem'
        }, {
          opacity: 1,
          y: 0,
          duration: gDur,
          ease: 'power3.out',
          onStart: () => {
            setIsHeadlineAnimGo(true);
          }
        },
        'headline-in'
      );
    }

    // ---------- Border
    if (bottomEl.current) {
      heroTl.add('border-in', `'-=${gDur}'`);
      heroTl.fromTo(
        bottomEl.current,
        {
          scaleY: 0,
          scaleX: 0
        }, {
          scaleY: 1,
          scaleX: 1,
          duration: gDur,
          ease: 'power3.out',
        },
        'headline-in'
      );
    }

    // ---------- Details
    if (detailsEl.current) {
      heroTl.add('details-in', `'-=${gDur*0.9}'`);
      heroTl.fromTo(
        detailsEl.current,
        {
          opacity: 0,
          y: '1rem'
        }, {
          opacity: 1,
          y: 0,
          duration: gDur/2,
          ease: 'power4.out',
        },
        'details-in'
      );
    }

    // -------------------- Timeline creation complete

    heroTl.play();

  }, [image, headline, location, locationSubtext]);

  // -------------------- Parallax

  useParallax({ containerRef: rootEl, start: 'top top', distancePx: 120, isEnabled: true });

  // -------------------- Render

  return (
    <section className={cx(["detail-hero"])} ref={rootEl}>
      <div className={cx("detail-hero__overlay")} ref={overlayEl} />
      <div className={cx("detail-hero__image-container")}>
        <div className={cx("detail-hero__image-wrapper")} ref={imageEl}>
          {isVideo ? (
            <video
              className={cx("detail-hero__video", "js-parallax")}
              poster={toProxiedWpUrl(videoThumbnail?.node?.mediaItemUrl) || ''}
              src={video}
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <Image image={image} className={`${cx("detail-hero__image")} js-parallax`} />
          )}
        </div>
      </div>
      <Container className={cx("detail-hero__container")}>
        <div className={cx("detail-hero__content")}>
          <div className={cx("headline-wrap")} ref={headlineEl}>
            <HeadlineAnim
              tag="h1"
              className={cx(["headline", "title-uc-xl"])}
              text={headline}
              isRevealEnabled={true}
              isInView={isHeadlineAnimGo}
            />
          </div>
          <div className={cx("details")} ref={detailsEl}>
            <DetailItem text={locationLabel} subtext={locationSubtext} />
          </div>
          <div className={cx("detail-hero__content__bottom")} ref={bottomEl} />
        </div>
      </Container>
    </section>
  );

}

DetailHeroFlexibleContent.fragments = {
  entry: gql`
    fragment DetailHeroFlexibleContent on FlexiblePageContentContentDetailHeroLayout {
      headline
      location
      locationSubtext
      assetType
      video
      videoThumbnail {
        ...ACFMedia
      }
      image {
        ...ACFMedia
      }
    }
  `
};
